import { defineStore } from 'pinia';
import { useLocalStorage, useSessionStorage } from '@vueuse/core';
import type { OrgOption, ChallengeName, LoginMethod } from './cookieAuthApi';

export const LOGIN_METHOD_STORAGE_KEY = 'auth:loginMethod';
// Kept per browser and through logout, so the login page can point a returning
// person at the method they used last time.
export const lastLoginMethod = useLocalStorage<LoginMethod | null>('auth:lastLoginMethod', null);

export default defineStore('loginFlow', () => {
  const orgs = ref<OrgOption[]>([]);
  // Set by the entry points beside setAuthMode, so a completed login is still
  // attributed to the IdP rather than to a password login. Persisted, unlike
  // the rest of this store: the select-org picker survives a hard reload by
  // re-probing GET /auth/session, whose pending branch carries no provider to
  // recover the method from.
  // flush 'sync' because the reload can land in the same tick as the write, and
  // no storage listener because useStorage answers its own write event by
  // pausing the write-watcher for a tick, which then swallows a same-tick $reset.
  const method = useSessionStorage<LoginMethod | null>(LOGIN_METHOD_STORAGE_KEY, null, {
    flush: 'sync',
    listenToStorageChanges: false,
  });
  const challengeName = ref<ChallengeName | null>(null);
  const cognitoSession = ref<string | null>(null);
  // Attributes Cognito requires with the challenge answer. Empty array means
  // send none; null means the BFF predates the field, so callers fall back to
  // send-everything. Keeping the two apart lets the frontend deploy before the
  // BFF without breaking new-user signup (FEDX-261).
  const requiredAttributes = ref<string[] | null>(null);
  const email = ref('');
  const isLoading = ref(false);

  function setOrgs(newOrgs: OrgOption[]) {
    orgs.value = newOrgs;
  }

  function setChallenge(name: ChallengeName, session: string, required: string[] | null = null) {
    challengeName.value = name;
    cognitoSession.value = session;
    requiredAttributes.value = required;
  }

  function setMethod(newMethod: LoginMethod) {
    method.value = newMethod;
  }

  function setEmail(newEmail: string) {
    email.value = newEmail;
  }

  function setLoading(loading: boolean) {
    isLoading.value = loading;
  }

  function $reset() {
    orgs.value = [];
    method.value = null;
    challengeName.value = null;
    cognitoSession.value = null;
    requiredAttributes.value = null;
    email.value = '';
    isLoading.value = false;
  }

  return {
    orgs,
    method,
    challengeName,
    cognitoSession,
    requiredAttributes,
    email,
    isLoading,
    setOrgs,
    setChallenge,
    setMethod,
    setEmail,
    setLoading,
    $reset,
  };
});
